import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  ScrollView,
  TouchableOpacity,
  Platform,
  Modal,
  ActivityIndicator,
  Image,
  Alert,
  Pressable,
  Animated,
  AppState,
} from 'react-native';
import type { V2Balance, V2CardDetailResponse, V2CreateCardDepositRequest } from '../../types/v2';
import SimpleHeader from '../../components/SimpleHeader';
import AmountCaret from '../../components/UIComponents/AmountCaret';
import TabBar from '../../components/TabBar';
import { CARD_SURFACE, COLORS } from '../../theme/themeTokens';
import { v2GetMyBalances, v2CreateCardDeposit, v2GetCard, v2GetCardWallet, v2GetCardDepositFeeRate, v2GetCardDepositRateFee, v2GetMinTopUpAmount, v2GetRates } from '../../services/v2/api';
import { realAccountName } from '../../utils/accountName';
import type { V2RateInfo } from '../../types/v2';
import { toUsdtAmount, formatUsdtAmount } from '../../utils/usdtEquivalent';
import { useCapabilities } from '../../hooks/useCapabilities';
import { useAuth } from '../../contexts/AuthContext';
import { usePIN } from '../../contexts/PINContext';
import PINConfirmModal from '../../components/UIComponents/PINConfirmModal';
import { useSwipeDownDismiss } from '../../hooks/useSwipeDownDismiss';
import { WEB_SHEET, WEB_SHEET_BACKDROP, WEB_SHEET_MODAL_ANIMATION } from '../../../web/sheet';
import NumericKeypad from '../../components/NumericKeypad';
import { compareByNetwork } from '../../utils/networkOrder';
import { formatAmount, parseAmount, numberToAmountDisplay } from '../../utils/amountFormat';
import { useIdempotencyKey } from '../../hooks/useIdempotencyKey';

// ──────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────



// ──────────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────────

interface DepositCardScreenProps {
  /** The card balance the deposit is going INTO (passed from CardScreen). */
  cardBalance: V2Balance;
  /** Raw Payoma card detail — used to show the card number / external id. */
  cardDetail: V2CardDetailResponse | null;
  onBack: () => void;
  onNavigate: (screen: string) => void;
  /** Called after a successful deposit creation. */
  onTransferSuccess: () => void;
  /** When true, parent (OperationsScreen) supplies header + tabbar. */
  embedded?: boolean;
  repeatPrefill?: { sourceBalanceId?: string; destinationBalanceId?: string; amount?: string; destinationAmount?: string };
}

// ──────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────

// Top inset used by the From/To picker sheets when rendered inside
// OperationsScreen. Aligns the sheet top just BELOW the screen's
// "From current account" / "To card" label so the header stays visible
// above the sheet. Breakdown:
//   status bar gutter (20 iOS / 28 Android)
//   + Operations header (44) + 12-dp gap + 44-dp tab strip   = +100
//   + scrollContent.paddingTop (16) + fieldLabel area (16)   = +32
//   + fieldLabel height (~22) + spacing below label (~8)     = +30
const OPERATIONS_TOP_INSET = (Platform.OS === 'android' ? 28 : 20) + 162;

/**
 * One row in the flat source picker — same shape as on WithdrawScreen so
 * the picker UI feels identical across Operations tabs.
 */
interface AccountPickerOption {
  key: string;
  symbol: string;
  networkSymbol: string;
  networkName: string;
  tokenName: string;
  iconUrl: string | null;
  amount: number;
  balance: V2Balance;
}

/** Format a number with space-separated thousands and comma decimal (design spec). */
const formatPretty = (value: number | string | null | undefined, fractionDigits = 2): string => {
  if (value === null || value === undefined) return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return String(value);
  const [intPart, decPart] = num.toFixed(fractionDigits).split('.');
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return decPart ? `${withSpaces},${decPart}` : withSpaces;
};

/** Extract the displayable USDT amount from a V2Balance. */
const balanceAmountNumber = (b: V2Balance): number => {
  // Use available_amount (amount − locked_amount) if present, so pending card deposits
  // are excluded from the displayed and validated balance.
  const raw = parseFloat(b.available_amount ?? b.amount);
  if (Number.isNaN(raw)) return 0;
  // Amount is already in major units in v2 — no precision dividing needed.
  return raw;
};

/** Try to pull a human card number / external id out of V2CardDetailResponse.
 *  Always returns a string (Payoma responses are loosely typed).
 *
 *  Field name precedence reflects what Payoma's test stand actually returns:
 *  `number` is the field that's present in real responses (verified
 *  empirically). The other names are legacy/fallback in case Payoma rotates
 *  the schema or older deployments still emit `masked_pan`/`pan`/etc. */
const resolveCardNumber = (cd: V2CardDetailResponse | null): string => {
  if (!cd) return '';
  const data: any = cd.data ?? {};
  const candidate =
    data?.number ??
    data?.masked_pan ??
    data?.card_number ??
    data?.pan ??
    data?.external_id ??
    '';
  if (candidate === null || candidate === undefined) return '';
  if (typeof candidate === 'object') return '';
  return String(candidate);
};

/** Resolve currency label for the "To card" row. Always returns a string —
 *  Payoma sometimes exposes currency as a `{code, iso4217, precision}` object
 *  under `data.wallet.currency` / `data.currency` instead of a plain string,
 *  which trips React's "Objects are not valid as a React child" rule if
 *  rendered directly. */
const resolveCardCurrency = (cd: V2CardDetailResponse | null): string => {
  if (!cd) return 'EUR';
  const data: any = cd.data ?? {};
  const walletCurrency = data?.wallet?.currency;
  if (typeof walletCurrency === 'string') return walletCurrency;
  if (walletCurrency && typeof walletCurrency === 'object') {
    return walletCurrency.symbol ?? walletCurrency.code ?? walletCurrency.iso4217 ?? 'EUR';
  }
  const rootCurrency = data?.currency;
  if (typeof rootCurrency === 'string') return rootCurrency;
  if (rootCurrency && typeof rootCurrency === 'object') {
    return rootCurrency.symbol ?? rootCurrency.code ?? rootCurrency.iso4217 ?? 'EUR';
  }
  return 'EUR';
};

/** Resolve card balance amount (major units). */
const resolveCardAmount = (cd: V2CardDetailResponse | null): number | null => {
  if (!cd) return null;
  const data: any = cd.data ?? {};
  const raw = data?.wallet?.balance?.amount;
  const precision: number = data?.wallet?.currency?.precision ?? 2;
  if (raw == null) return null;
  return raw / Math.pow(10, precision);
};

// How long the form waits for the accounts and the two rates before showing
// itself anyway. Long enough for a slow network, short enough that a request
// that never answers doesn't hold the screen hostage.
const FORM_LOADING_TIMEOUT_MS = 8000;

// ──────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────

const DepositCardScreen: React.FC<DepositCardScreenProps> = ({
  cardBalance,
  cardDetail,
  onBack,
  onNavigate,
  onTransferSuccess,
  embedded = false,
  repeatPrefill,
}) => {
  const statusBarHeight = Platform.OS === 'android' ? 28 : 0;

  // ── Source picker — flat (token × network) options, same model as
  //    WithdrawScreen. Scoped to USDT-only because the card-top-up flow
  //    converts USDT→EUR; other source tokens would break the math.
  const [pickerOptions, setPickerOptions] = useState<AccountPickerOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<AccountPickerOption | null>(null);
  const [pickerModalVisible, setPickerModalVisible] = useState(false);
  // Swipe-to-dismiss for the "From" current-accounts picker. Shared
  // hook keeps the gesture identical to NumericKeypad / Withdraw.
  const fromSwipe = useSwipeDownDismiss(
    pickerModalVisible,
    () => setPickerModalVisible(false),
  );
  const [balancesLoading, setBalancesLoading] = useState(true);

  // ── Card picker — only rendered when the user owns more than one card.
  //    `selectedCard` defaults to the cardBalance prop the parent passed in.
  const [cardOptions, setCardOptions] = useState<V2Balance[]>([]);
  const [selectedCard, setSelectedCard] = useState<V2Balance>(cardBalance);
  const [cardModalVisible, setCardModalVisible] = useState(false);
  // Swipe-to-dismiss for the "To card" picker.
  const cardSwipe = useSwipeDownDismiss(
    cardModalVisible,
    () => setCardModalVisible(false),
  );

  // Success modal — same UX as the Withdraw tab. Amount + currency are
  // snapshotted at submit time so the displayed figures don't change if
  // the user edits inputs before tapping OK.
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [submittedAmount, setSubmittedAmount] = useState<number>(0);
  const [submittedCurrency, setSubmittedCurrency] = useState<string>('');
  // Last-4 PAN digits per card (wallet_address → "1234"). Populated by a
  // parallel v2GetCard fan-out over cardOptions; same mechanism the
  // Dashboard card tiles use. Rendered as "**** 1234" on the To-card tile.
  const [cardLast4, setCardLast4] = useState<Record<string, string>>({});
  // Card name per card id, from the same fan-out. The picker used to title
  // its rows with account_name, which for a card account is its type code —
  // every row read "card_balance".
  const [cardNames, setCardNames] = useState<Record<string, string>>({});

  // Bridge for existing logic that still references `selectedFrom` — it's
  // the V2Balance underlying the currently picked option.
  const selectedFrom: V2Balance | null = selectedOption?.balance ?? null;

  // ── Amount inputs ──
  // `fromInput` holds the USDT amount the user is debiting from their current
  // account. `toInput` holds the EUR amount that will land on the card. They
  // are linked by USDT_TO_EUR_RATE: editing From auto-recomputes To. Storing
  // both as strings lets the user keep partial inputs ("0.", "0,5") without
  // jumping the cursor. `fromInput` is the source of truth for submit/fee/total.
  const [fromInput, setFromInput] = useState('0');
  const [toInput, setToInput] = useState('0');

  // Which amount field the custom NumericKeypad is currently editing. null →
  // sheet closed. The keypad shares its onChange with handleFromChange /
  // handleToChange so the cross-conversion (From↔To) keeps working exactly
  // like before.
  const [keypadField, setKeypadField] = useState<'from' | 'to' | null>(null);

  // Which side of the conversion the user typed in last. Drives the inline
  // (read-only) radio markers next to "From current account" / "To card"
  // labels and is sent to the backend (conversion_direction column).
  // null = no typing yet → neither radio active, field omitted on submit.
  // Updated only by handleFromChange / handleToChange — the radios themselves
  // are display-only, no manual flip.
  const [conversionDirection, setConversionDirection] =
    useState<'USDT-EUR' | 'EUR-USDT' | null>(null);

  // ── Submission state ──
  const [submitting, setSubmitting] = useState(false);
  const [pinModalVisible, setPinModalVisible] = useState(false);

  // PIN-confirm — если PIN не настроен, submit идёт сразу (как было).
  const { isPINSetup, isPINSkipped } = usePIN();
  // One attempt per top-up: a press after a lost answer resends it with the
  // same key, and the server does not file it twice.
  const idempotency = useIdempotencyKey<V2CreateCardDepositRequest>();

  // ── Card wallet name ──
  const [cardWalletName, setCardWalletName] = useState<string | null>(null);

  // Card-deposit commission rate fetched from the admin-managed fee_settings
  // table (code "card_deposit"). `null` until the request lands — no client-
  // side default, so the UI doesn't show a wrong "Fee X%" before the actual
  // rate is known. Submit is gated on this being loaded.
  const [feeRate, setFeeRate] = useState<number | null>(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await v2GetCardDepositFeeRate();
        const n = parseFloat(res.percent);
        if (mounted && Number.isFinite(n) && n >= 0) setFeeRate(n);
      } catch {
        // Silent — UI just keeps the Fee row hidden + the submit button
        // disabled until the rate is known.
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Rate haircut (v1_2_fee_settings.code = "card_deposit_rate"). Applied
  // ТОЛЬКО в math: effectiveRate = convRate × (1 - rateFee) × (1 - feeRate).
  // "Currency rate" row в UI по-прежнему показывает raw convRate — юзер
  // проектному requirement'у "ничего не отображаем, просто вычисляем".
  // Fallback 0 (эквивалент "no haircut") на fetch-error / row-missing —
  // submit не gate'им, потому что 0 = как раньше.
  const [rateFee, setRateFee] = useState(0);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await v2GetCardDepositRateFee();
        const n = parseFloat(res.percent);
        if (mounted && Number.isFinite(n) && n >= 0) setRateFee(n);
      } catch {
        // Silent — keep rateFee=0 (no haircut) на любую ошибку.
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Live "Minimum amount" threshold from v1_2_amount_settings
  // ("min_amount_top_up"). Threshold is denominated in USDT; the input
  // amount is converted to USDT via the live rates dict before the gate.
  const [minAmount, setMinAmount] = useState<number | null>(null);
  const [rates, setRates] = useState<Record<string, V2RateInfo>>({});
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await v2GetMinTopUpAmount();
        const n = parseFloat(res.value);
        if (mounted && Number.isFinite(n) && n >= 0) setMinAmount(n);
      } catch {
        // Silent — minimum-amount row stays hidden if the request fails.
      }
    })();
    (async () => {
      try {
        const res = await v2GetRates();
        if (mounted) setRates(res.rates ?? {});
      } catch {
        // Silent — gate fails-open when rates miss.
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Live USDT→EUR conversion rate fetched once from GET /api/v2/rates.
  // The server stores rates as "USDT per 1 EUR" (e.g. 1.18 means 1 EUR =
  // 1.18 USDT), so we invert to get "EUR per 1 USDT" (≈ 0.847). `null`
  // until the request lands — gates submit just like feeRate.
  const [convRate, setConvRate] = useState<number | null>(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await v2GetRates(['EUR']);
        const usdtPerEur = parseFloat(res.rates?.EUR?.price_usd ?? '');
        if (mounted && Number.isFinite(usdtPerEur) && usdtPerEur > 0) {
          setConvRate(1 / usdtPerEur);
        }
      } catch {
        // Silent — submit stays disabled until the rate is known.
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const cardId = selectedCard.wallet_address ?? '';
    if (!cardId) {
      setCardWalletName(null);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const res = await v2GetCardWallet(cardId);
        const name: string = res?.data?.name ?? '';
        if (mounted) setCardWalletName(name || null);
      } catch {
        // non-critical — wallet name is optional
      }
    })();
    return () => { mounted = false; };
  }, [selectedCard.wallet_address]);

  // The form waits for everything it needs before it appears: the accounts,
  // the fee rate and the conversion rate all arrive in separate requests,
  // and rendering as they trickle in showed empty tiles, a zero rate and a
  // dead Confirm button — the screen read as broken.
  //
  // The two rate requests swallow their errors and leave null behind, so the
  // wait is capped: after it, the form appears with whatever landed rather
  // than spinning forever.
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setLoadingTimedOut(true), FORM_LOADING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  // Balances load on open and again each time the app comes back to the
  // foreground (fresh figures after a trip to the bank for a code). Only the
  // first load picks the card and the source; a reload keeps what is picked
  // and only refreshes its figures — unless it has gone from the list.
  const balancesLoadedOnce = useRef(false);

  const loadBalances = useCallback(async () => {
    try {
      setBalancesLoading(true);
      const res = await v2GetMyBalances();
      const allBalances = res.balances ?? [];

      // Source picker — current-account, non-virtual USDT rows, one per
      // (token × network). Card-deposit conversion math is USDT-only, so
      // we keep the filter narrow here even though the picker layout
      // mirrors WithdrawScreen (which lists every token).
      const currentRealUsdt = allBalances.filter(
        (b) =>
          (b.account_type_code ?? 'current') === 'current' &&
          b.provider_name !== 'virtual' &&
          b.token_symbol?.toUpperCase() === 'USDT',
      );
      const opts: AccountPickerOption[] = currentRealUsdt.map((b) => ({
        key: `${b.token_symbol}|${b.network_symbol || 'no-net'}|${b.wallet_address || b.wallet_id || b.id}`,
        symbol: b.token_symbol,
        networkSymbol: b.network_symbol || '',
        networkName: b.network_name || '',
        tokenName: b.token_name || b.token_symbol,
        iconUrl: b.icon_url ?? null,
        // Use available_amount (amount − locked_amount) so the picker value
        // matches the form's Available row; raw `amount` would double-count
        // pending top-up locks.
        amount: parseFloat(b.available_amount ?? b.amount ?? '0') || 0,
        balance: b,
      }));
      // Network ordering: TRON first, Ethereum second, everything else
      // after (alphabetical). Logic is shared with WithdrawScreen via
      // utils/networkOrder — adjust the rank map there, not here.
      opts.sort(compareByNetwork);
      setPickerOptions(opts);
      const firstLoad = !balancesLoadedOnce.current;
      const defaultOption =
        opts.find((option) => option.balance.id === repeatPrefill?.sourceBalanceId) ?? opts[0] ?? null;
      setSelectedOption((prev) =>
        (!firstLoad && prev && opts.find((option) => option.balance.id === prev.balance.id)) || defaultOption,
      );

      // Card picker — all card-type balances the user owns. Pre-select the
      // one App.tsx handed us via the prop, falling back to first.
      const cards = allBalances.filter(
        (b) =>
          b.account_type_code === 'card' || b.account_type_code === 'card_balance',
      );
      setCardOptions(cards);
      const preselectedCard =
        cards.find((c) => c.id === repeatPrefill?.destinationBalanceId) ??
        cards.find((c) => c.id === cardBalance.id) ?? cards[0] ?? cardBalance;
      setSelectedCard((prev) =>
        (!firstLoad && cards.find((c) => c.id === prev.id)) || preselectedCard,
      );
      balancesLoadedOnce.current = true;
    } catch (err) {
      console.warn('[DepositCardScreen] failed to load balances', err);
    } finally {
      setBalancesLoading(false);
    }
  }, [cardBalance.id, repeatPrefill?.destinationBalanceId, repeatPrefill?.sourceBalanceId]);

  useEffect(() => {
    if (repeatPrefill?.amount || repeatPrefill?.destinationAmount) {
      // The repeated amount is the client's own choice: the minimum seeded
      // once the rate arrives must not replace it a second later.
      amountTouched.current = true;
    }
    if (repeatPrefill?.amount) {
      setFromInput(formatAmount(repeatPrefill.amount));
    }
    if (repeatPrefill?.destinationAmount) {
      setToInput(formatAmount(repeatPrefill.destinationAmount));
    }
  }, [repeatPrefill?.amount, repeatPrefill?.destinationAmount]);

  // Fan-out per card in the picker: the PAN's last 4 digits from
  // `v2GetCard` — the same call the Dashboard uses — and the card's name
  // from `v2GetCardWallet`. The name was fetched for the selected card
  // only, which left the picker's other rows with nothing to be called.
  useEffect(() => {
    if (cardOptions.length === 0) return;
    let cancelled = false;
    (async () => {
      const map: Record<string, string> = {};
      const names: Record<string, string> = {};
      await Promise.all(
        cardOptions.map(async (c) => {
          const cardId = c.wallet_address;
          if (!cardId) return;
          const [cardRes, walletRes] = await Promise.allSettled([
            v2GetCard(cardId),
            v2GetCardWallet(cardId),
          ]);
          if (cardRes.status === 'fulfilled') {
            const data: any = cardRes.value?.data ?? {};
            const panCandidate =
              data?.number ??
              data?.masked_pan ??
              data?.pan ??
              data?.card_number ??
              data?.external_id ??
              '';
            const digits = String(panCandidate).replace(/\D/g, '');
            if (digits.length >= 4) {
              map[cardId] = digits.slice(-4);
            }
          }
          if (walletRes.status === 'fulfilled') {
            const name = walletRes.value?.data?.name;
            if (typeof name === 'string' && name.trim().length > 0) {
              names[cardId] = name.trim();
            }
          }
        }),
      );
      if (cancelled) return;
      if (Object.keys(map).length > 0) {
        setCardLast4((prev) => ({ ...prev, ...map }));
      }
      if (Object.keys(names).length > 0) {
        setCardNames((prev) => ({ ...prev, ...names }));
      }
    })();
    return () => { cancelled = true; };
  }, [cardOptions]);

  useEffect(() => {
    loadBalances();
    // Re-fetch balances when app comes back to foreground (e.g. after admin action)
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') loadBalances();
    });
    return () => subscription.remove();
  }, [loadBalances]);

  // ── Derived values ──
  // `fromInput` is the source-of-truth for the request body. Both inputs use
  // the "1 234,56" display format; strip the thousands spaces and convert
  // the comma to a dot so parseFloat returns a usable number. Inlined here
  // (instead of calling parseAmount) because parseAmount is declared further
  // down in this function body and would hit the TDZ on first render.
  const amountNumber = useMemo(() => {
    const n = parseFloat(fromInput.replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [fromInput]);

  // Zero when the rate hasn't loaded yet — keeps Total math sane while the
  // submit button stays disabled, so the user can't fire a deposit before
  // the real rate is known.
  const feeNumber = useMemo(
    () => (feeRate != null ? amountNumber * feeRate : 0),
    [amountNumber, feeRate],
  );

  const fromBalanceNum = selectedFrom ? balanceAmountNumber(selectedFrom) : 0;
  // True when the user typed (or the conversion produced) more than the
  // selected current account holds. Drives both the inline error text
  // under the tile AND the red colour on the amount + currency suffix.
  // Comparison is in cents (integers) to dodge FP imprecision — e.g.
  // parseFloat("42956.44") * 100 can land on 4295643.9999…, which would
  // make the displayed-equal-to-balance case spuriously trip as "over".
  const insufficientFunds =
    Boolean(selectedFrom) &&
    amountNumber > 0 &&
    Math.round(amountNumber * 100) > Math.round(fromBalanceNum * 100);
  const toCardNumber = resolveCardNumber(cardDetail);
  const toCardCurrency = resolveCardCurrency(cardDetail);
  const toCardAmount = resolveCardAmount(cardDetail);

  // Transfer allowed only when a source is selected, amount is positive, and
  // source has enough funds for the amount (fee is INSIDE amount under the
  // new model — no separate "+ fee" addition needed). Also requires the fee
  // rate to have loaded so the To preview reflects the actual rate.
  // can_create_card_deposit is server-derived: false under impersonation.
  const caps = useCapabilities();
  // Minimum-amount gate is only enforced once `minAmount` has loaded so a
  // misconfigured / unreachable endpoint can't permanently lock the form.
  // Min is in USDT — convert the entered amount via live rates; unknown
  // rate → fail-open so a flaky /rates response can't block the submit.
  const amountInUsdt = selectedFrom
    ? toUsdtAmount(amountNumber, selectedFrom.token_symbol, rates)
    : null;
  const meetsMinimumTopUp =
    minAmount == null || amountInUsdt == null || amountInUsdt >= minAmount;
  // Explicit impersonation-guard в дополнение к caps: под impersonation backend
  // hard-deny'ит POST /card-deposits (403). caps.can_create_card_deposit уже
  // должен вернуть false, но belt-and-suspenders на случай устаревшего auth-state.
  const { isImpersonating } = useAuth();
  // Everything the form needs: the accounts and both rates. Until it is all
  // here the screen shows a loader instead of half-filled tiles.
  const formDataReady = !balancesLoading && feeRate != null && convRate != null;
  const showFormLoader = !formDataReady && !loadingTimedOut;

  const canSubmit =
    !submitting &&
    selectedFrom != null &&
    amountNumber > 0 &&
    feeRate != null &&
    convRate != null &&
    Math.round(amountNumber * 100) <= Math.round(fromBalanceNum * 100) &&
    meetsMinimumTopUp &&
    caps.can_create_card_deposit &&
    !isImpersonating;

  // ── Handlers ──
  const handleSelectOption = (opt: AccountPickerOption) => {
    setSelectedOption(opt);
    setPickerModalVisible(false);
  };

  const handleSelectCard = (card: V2Balance) => {
    setSelectedCard(card);
    setCardModalVisible(false);
  };

  // formatAmount / parseAmount / numberToAmountDisplay are shared with
  // WithdrawScreen via utils/amountFormat — adjust the formatting rules
  // there, not here.

  // Fee-inside-amount model: From (USDT) is the total debit. The
  // convertable portion sent to the card is (From × (1 - feeRate)), so the
  // effective USDT→EUR rate seen by the user is convRate × (1 - feeRate).
  // Дополнительный rate haircut card_deposit_rate (0.05 % by default)
  // применяется поверх — user получает меньше EUR на карту. Row "Currency
  // rate" в UI по-прежнему показывает raw convRate (см. rateFee state).
  // Pre-fetch (либо convRate null, либо feeRate null) → treat как 0 для
  // preview'а; canSubmit блокирует реальный submit до landing'а обеих ставок.
  const effectiveRate = (convRate ?? 0) * (1 - rateFee) * (1 - (feeRate ?? 0));

  // Whether the client has touched an amount yet. The minimum is seeded
  // into the From field once; after that the field is theirs, and seeding
  // over a typed figure would be worse than never seeding at all.
  const amountTouched = useRef(false);

  // From → To: USDT debit minus fee → EUR landing on card. Any keystroke
  // here means "user typed in the From side" → direction = USDT-EUR.
  const handleFromChange = (raw: string) => {
    const v = formatAmount(raw);
    setFromInput(v);
    setConversionDirection('USDT-EUR');
    const n = parseAmount(v);
    if (n > 0) {
      setToInput(numberToAmountDisplay(n * effectiveRate));
    } else {
      setToInput('0');
    }
  };

  // To → From: reverse. User entering an EUR target sees the gross USDT
  // they must surrender (which already includes the fee they'll pay).
  // Direction flips to EUR-USDT because the user is typing the destination.
  const handleToChange = (raw: string) => {
    const v = formatAmount(raw);
    setToInput(v);
    setConversionDirection('EUR-USDT');
    const n = parseAmount(v);
    if (n > 0 && effectiveRate > 0) {
      setFromInput(numberToAmountDisplay(n / effectiveRate));
    } else {
      setFromInput('0');
    }
  };

  // The From field opened at zero and the client had to find out the
  // minimum from the row below and type it. It starts at the minimum now,
  // which fills the card side through the same path a keystroke takes.
  //
  // It waits for the rate: seeding before it lands would put an amount in
  // one field and nothing in the other. It seeds once — a later rate
  // refresh must not overwrite what the client has since typed — and it
  // seeds even when the balance will not cover it, because that is exactly
  // when knowing the minimum matters; the insufficient-funds line says the
  // rest. The source here is always USDT, so the setting's figure goes in
  // as it is.
  const minSeeded = useRef(false);
  useEffect(() => {
    if (minSeeded.current || amountTouched.current) { return; }
    if (minAmount == null || convRate == null || feeRate == null) { return; }
    minSeeded.current = true;
    handleFromChange(numberToAmountDisplay(minAmount));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minAmount, convRate, feeRate]);

  const handleTransfer = () => {
    if (!canSubmit || !selectedFrom) return;
    if (!isPINSetup || isPINSkipped) {
      executeTransfer();
    } else {
      setPinModalVisible(true);
    }
  };

  const executeTransfer = async () => {
    if (!selectedFrom) return;
    setPinModalVisible(false);
    try {
      setSubmitting(true);
      const request: V2CreateCardDepositRequest = {
        destination_balance_id: selectedCard.id, // target card balance — credited on completion
        balance_id: selectedFrom.id, // source balance to lock funds from
        currency: selectedFrom.token_symbol,
        amount: amountNumber.toString(),
        // Fee is advisory; backend overrides with the hardcoded 2%.
        fee: feeNumber.toString(),
        // EUR-side metadata for OneAdmin visibility. conversion_rate is the
        // live CoinMarketCap USDT→EUR rate that was applied at submit time
        // (NOT the effective-after-fee rate; OneAdmin stores it raw so the
        // historical record is reconstructable).
        destination_amount: parseAmount(toInput).toString(),
        conversion_rate: (convRate ?? 0).toString(),
        // Which side the user typed in last on the form — drives the
        // OneAdmin transaction modal's "Direction" field. Omitted when
        // the user didn't type at all (e.g. tap-Max-only deposit) so the
        // backend stores NULL rather than a fabricated default.
        ...(conversionDirection ? { conversion_direction: conversionDirection } : {}),
      };
      // The rate and the converted amount drift with the market; the top-up is
      // the same while the card, the account and the amount are.
      const attempt = idempotency.attempt(request, {
        destination_balance_id: request.destination_balance_id,
        balance_id: request.balance_id,
        amount: request.amount,
      });
      await v2CreateCardDeposit(attempt.request, attempt.key);
      idempotency.answered();
      // Snapshot for the success modal — same UX as the Withdraw tab.
      setSubmittedAmount(amountNumber);
      setSubmittedCurrency(selectedFrom.token_symbol);
      setSuccessModalVisible(true);
    } catch (err: any) {
      idempotency.failed(err);
      console.warn('[DepositCardScreen] v2CreateCardDeposit failed', err);
      Alert.alert('Transfer failed', err?.message ?? 'Could not create card deposit.');
    } finally {
      setSubmitting(false);
    }
  };

  // ──────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />

      {!embedded && (
        <View style={[styles.fixedHeader, { paddingTop: statusBarHeight }]}>
          <SimpleHeader
            title="Top-up card"
            onBack={onBack}
            backgroundVariant="primaryNavy"
          />
        </View>
      )}

      <View style={styles.bottomOverscrollCap} />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {showFormLoader ? (
          <View style={styles.formLoading}>
            <ActivityIndicator size="large" color={COLORS.brandOrange} />
          </View>
        ) : (
        /* Tap-anywhere-to-close wrap for the inline NumericKeypad. Nested
            TouchableOpacity (From / To tap-targets, picker tiles, Confirm,
            etc.) absorb their own touches so this Pressable only fires
            when the user taps an inert region of the form. Pressable
            doesn't intercept scroll gestures — only taps. */
        <Pressable onPress={() => setKeypadField(null)} accessible={false}>
        <View style={styles.formCard}>
          {/* From current account — picker on the left + inline amount input
              on the right. Tapping the left half opens the source modal;
              the right half stays a TextInput so users can type the amount
              without an extra row below. */}
          <Text style={styles.fieldLabel}>From current account</Text>
          {balancesLoading ? (
            <View style={[styles.pickerTile, styles.pickerTileCenter]}>
              <ActivityIndicator size="small" color={COLORS.brandOrange} />
            </View>
          ) : selectedOption ? (
            <View style={styles.pickerTile}>
              <TouchableOpacity
                style={styles.pickerSelectArea}
                onPress={() => setPickerModalVisible(true)}
                activeOpacity={pickerOptions.length > 1 ? 0.7 : 1}
                disabled={pickerOptions.length <= 1}
              >
                <TokenIcon
                  symbol={selectedOption.symbol}
                  iconUrl={selectedOption.iconUrl ?? undefined}
                />
                <View style={styles.pickerTextCol}>
                  <View style={styles.pickerTitleRow}>
                    <Text style={styles.pickerTitle}>
                      {selectedOption.networkSymbol
                        ? `${selectedOption.symbol} - ${selectedOption.networkSymbol}`
                        : selectedOption.symbol}
                    </Text>
                    {pickerOptions.length > 1 ? (
                      <Image
                        source={require('../../assets/arrow_left_20.png')}
                        style={styles.pickerChevronInline}
                        resizeMode="contain"
                      />
                    ) : null}
                  </View>
                  {selectedOption.networkName ? (
                    <Text style={styles.pickerSubtitle}>{selectedOption.networkName}</Text>
                  ) : null}
                </View>
              </TouchableOpacity>
              {/* Tap target — opens the custom NumericKeypad sheet. The Text
                  renders fromInput verbatim; an empty string falls back to "0"
                  so the field never appears blank even between key presses.
                  hitSlop expands the touchable region well past the visible
                  digits so the input is comfortable to tap with a thumb,
                  even when the value is just "0". */}
              {/* Tap area now wraps both the amount Text and the currency
                  suffix, so a tap on "USDT" opens the keypad too. The
                  blinking cursor sits between them when the From field
                  is the active keypad target. */}
              <TouchableOpacity
                onPress={() => !submitting && setKeypadField('from')}
                disabled={submitting}
                activeOpacity={0.7}
                hitSlop={{ top: 16, bottom: 16, left: 0, right: 16 }}
                style={styles.pickerAmountTapArea}
              >
                <Text
                  testID="deposit-card-from-amount"
                  style={[styles.pickerAmountInput, insufficientFunds && styles.amountError]}
                >
                  {fromInput || '0'}
                </Text>
                <AmountCaret active={keypadField === 'from'} />
                <Text style={[styles.pickerAmountSuffix, insufficientFunds && styles.amountError]}>
                  {selectedOption.symbol}
                </Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={[styles.pickerTile, styles.pickerTileCenter]}>
              <Text style={styles.fromSubtitle}>No USDT balance available</Text>
            </View>
          )}

          {/* Insufficient-funds hint — appears only when the user typed an
              amount larger than the selected current account's available
              balance. Confirm stays disabled by canSubmit either way. */}
          {insufficientFunds && selectedFrom ? (
            <Text style={styles.insufficientFundsText}>
              Insufficient funds on the current account. Available: {formatPretty(fromBalanceNum)} {selectedFrom.token_symbol}
            </Text>
          ) : null}

          {/* To card — same tile style as "From current account". Left half
              is tappable when the user owns multiple cards (opens the card
              picker); the amount input stays on the right of the tile. */}
          <Text style={[styles.fieldLabel, styles.fieldLabelSpaced]}>To card</Text>
          {/* The "To card" tile is the last sibling that drives the
              uniform 16 dp rhythm below — bumped marginBottom from 8
              to 16 so the gap to the next element (Available / info /
              Expected, all of which are conditional) stays 16 dp no
              matter which combination is rendered. Followers below
              own no marginTop. */}
          <View style={[styles.pickerTile, { marginBottom: 16 }]}>
            <TouchableOpacity
              style={styles.pickerSelectArea}
              onPress={() => setCardModalVisible(true)}
              activeOpacity={cardOptions.length > 1 ? 0.7 : 1}
              disabled={cardOptions.length <= 1}
            >
              <TokenIcon
                symbol={selectedCard.token_symbol}
                iconUrl={selectedCard.icon_url ?? undefined}
              />
              <View style={styles.pickerTextCol}>
                <View style={styles.pickerTitleRow}>
                  <Text style={styles.pickerTitle}>
                    {cardWalletName || realAccountName(selectedCard) || selectedCard.token_symbol}
                  </Text>
                  {cardOptions.length > 1 ? (
                    <Image
                      source={require('../../assets/arrow_left_20.png')}
                      style={styles.pickerChevronInline}
                      resizeMode="contain"
                    />
                  ) : null}
                </View>
                {(() => {
                  // Subtitle: masked last4 from the PAN fan-out (matches
                  // Dashboard card tiles). Falls back to the account's own
                  // name while the PAN is still on its way — its type code
                  // is not a name and never appears here.
                  const last4 = selectedCard.wallet_address
                    ? cardLast4[selectedCard.wallet_address]
                    : undefined;
                  if (last4) {
                    return <Text style={styles.pickerSubtitle}>**** {last4}</Text>;
                  }
                  const own = realAccountName(selectedCard);
                  if (own && cardWalletName) {
                    return <Text style={styles.pickerSubtitle}>{own}</Text>;
                  }
                  return null;
                })()}
              </View>
            </TouchableOpacity>
            {/* Tap target — opens the same NumericKeypad with the "to" field
                bound. handleToChange runs the reverse conversion (EUR → USDT)
                exactly like before. hitSlop matches the From input above so
                both tap zones feel equivalent. */}
            {/* Same wrapping as the From field — currency suffix now
                inside the tap area + cursor when this field is active. */}
            <TouchableOpacity
              onPress={() => !submitting && setKeypadField('to')}
              disabled={submitting}
              activeOpacity={0.7}
              hitSlop={{ top: 16, bottom: 16, left: 24, right: 12 }}
              style={styles.pickerAmountTapArea}
            >
              <Text style={styles.pickerAmountInput}>
                {toInput || '0'}
              </Text>
              <AmountCaret active={keypadField === 'to'} />
              <Text style={styles.pickerAmountSuffix}>{toCardCurrency}</Text>
            </TouchableOpacity>
          </View>

          {/* Available row — under To card. Mirrors the layout used under
              "From current account". Shows the current card balance from
              the Payoma sync; hidden when the value is unknown. */}
          {toCardAmount != null && (
            <View style={styles.availableRow}>
              <Text style={styles.availableLabel}>Available</Text>
              <Text style={styles.availableValue}>
                {formatPretty(toCardAmount)} {toCardCurrency}
              </Text>
            </View>
          )}

          {/* Combined info tile — Minimum amount + Fee, stacked
              in a single card with hairline dividers. Currency rate строку
              убрали по UX-запросу — юзеру достаточно видеть Fee и итоговое
              To-card значение. */}
          {selectedFrom && (feeRate != null || minAmount != null) ? (
            <View style={[styles.infoCard, styles.infoCardFlat]}>
              {minAmount != null ? (
                <View style={styles.infoCardRow}>
                  <Text style={styles.infoCardLabel}>Minimum amount</Text>
                  <Text style={styles.infoCardValue}>
                    {formatUsdtAmount(minAmount, 0)}
                  </Text>
                </View>
              ) : null}

              {minAmount != null && feeRate != null ? (
                <View style={styles.infoCardDivider} />
              ) : null}

              {feeRate != null ? (
                <View style={styles.infoCardRow}>
                  <Text style={styles.infoCardLabel}>
                    Fee, {(feeRate * 100).toFixed(2).replace(/\.?0+$/, '')}% (included)
                  </Text>
                  <Text style={styles.infoCardValue}>
                    {formatPretty(feeNumber)} {selectedFrom.token_symbol}
                  </Text>
                </View>
              ) : null}
            </View>
          ) : null}

          {/* Expected-processing callout — clone of the Withdraw tab; the
              duration is shorter for card top-ups ("up to 3 hours"). */}
          <View style={styles.expectedFrame}>
            <Image
              source={require('../../assets/clock.png')}
              style={styles.expectedIcon}
              resizeMode="contain"
            />
            <Text style={styles.expectedText}>
              Expected processing: <Text style={styles.expectedStrong}>up to 3 hours</Text> (working days)
            </Text>
          </View>

          {/* Confirm button — same primary-navy CTA as on the Withdraw tab. */}
          <TouchableOpacity
            style={[styles.confirmBtn, !canSubmit && styles.confirmBtnDisabled]}
            activeOpacity={0.85}
            onPress={handleTransfer}
            disabled={!canSubmit}
          >
            {submitting ? (
              <ActivityIndicator color={COLORS.white} />
            ) : (
              <Text style={styles.confirmBtnText}>Confirm transfer</Text>
            )}
          </TouchableOpacity>
        </View>
        </Pressable>
        )}
      </ScrollView>

      {!embedded && <TabBar activeTab="dashboard" onNavigate={onNavigate} />}

      {/* Source picker — flat (USDT × network) list, same UX as Withdraw. */}
      <Modal
        visible={pickerModalVisible}
        transparent
        animationType={WEB_SHEET_MODAL_ANIMATION ?? 'slide'}
        statusBarTranslucent
        onRequestClose={() => setPickerModalVisible(false)}
      >
        <Pressable style={[styles.modalOverlay, WEB_SHEET_BACKDROP]} onPress={() => setPickerModalVisible(false)}>
          <Animated.View
            style={[
              styles.modalSheet,
              embedded && { marginTop: OPERATIONS_TOP_INSET },
              WEB_SHEET,
              { transform: [{ translateY: fromSwipe.translateY }] },
            ]}
            {...fromSwipe.panHandlers}
          >
            <Pressable onPress={(e) => e.stopPropagation()}>
              <View style={styles.modalHandle} />
              <View style={styles.modalTitleRow}>
                <Text style={styles.modalTitle}>Current accounts</Text>
                <Text style={styles.modalTitleAside}>Available balance</Text>
              </View>
              {pickerOptions.length === 0 ? (
                <Text style={styles.modalEmpty}>No USDT balance available.</Text>
              ) : null}
              {pickerOptions.map((opt) => {
                const isSelected = selectedOption?.key === opt.key;
                const title = opt.networkSymbol
                  ? `${opt.symbol} - ${opt.networkSymbol}`
                  : opt.symbol;
                return (
                  <TouchableOpacity
                    key={opt.key}
                    style={[styles.modalOption, isSelected && styles.modalOptionSelected]}
                    onPress={() => handleSelectOption(opt)}
                    activeOpacity={0.7}
                  >
                    <TokenIcon symbol={opt.symbol} iconUrl={opt.iconUrl ?? undefined} />
                    <View style={styles.modalOptionText}>
                      <Text style={styles.modalOptionName}>{title}</Text>
                      {opt.networkName ? (
                        <Text style={styles.modalOptionSubtitle}>{opt.networkName}</Text>
                      ) : null}
                    </View>
                    <Text style={styles.modalOptionAmount}>
                      {formatPretty(opt.amount)} {opt.symbol}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </Pressable>
          </Animated.View>
        </Pressable>
      </Modal>

      {/* Card picker — only mounted when the user has more than one card.
          Shown above so a 1-card flow keeps the legacy display untouched. */}
      <Modal
        visible={cardModalVisible}
        transparent
        animationType={WEB_SHEET_MODAL_ANIMATION ?? 'slide'}
        statusBarTranslucent
        onRequestClose={() => setCardModalVisible(false)}
      >
        <Pressable style={[styles.modalOverlay, WEB_SHEET_BACKDROP]} onPress={() => setCardModalVisible(false)}>
          <Animated.View
            style={[
              styles.modalSheet,
              embedded && { marginTop: OPERATIONS_TOP_INSET },
              WEB_SHEET,
              { transform: [{ translateY: cardSwipe.translateY }] },
            ]}
            {...cardSwipe.panHandlers}
          >
            <Pressable onPress={(e) => e.stopPropagation()}>
              <View style={styles.modalHandle} />
              <View style={styles.modalTitleRow}>
                <Text style={styles.modalTitle}>Select card</Text>
              </View>
              {cardOptions.map((card) => {
                const isSelected = selectedCard.id === card.id;
                // The card's own name, then the currency. account_name is
                // the account's type code for a card, so it is filtered
                // out — it is what made every row read "card_balance".
                const title = (card.wallet_address ? cardNames[card.wallet_address] : undefined)
                  || realAccountName(card)
                  || card.token_name
                  || card.token_symbol;
                const last4 = card.wallet_address ? cardLast4[card.wallet_address] : undefined;
                const subtitle = last4 ? `**** ${last4}` : (card.network_name || card.token_symbol);
                return (
                  <TouchableOpacity
                    key={card.id}
                    style={[styles.modalOption, isSelected && styles.modalOptionSelected]}
                    onPress={() => handleSelectCard(card)}
                    activeOpacity={0.7}
                  >
                    <TokenIcon symbol={card.token_symbol} iconUrl={card.icon_url ?? undefined} />
                    <View style={styles.modalOptionText}>
                      <Text style={styles.modalOptionName}>{title}</Text>
                      {subtitle ? (
                        <Text style={styles.modalOptionSubtitle}>{subtitle}</Text>
                      ) : null}
                    </View>
                  </TouchableOpacity>
                );
              })}
            </Pressable>
          </Animated.View>
        </Pressable>
      </Modal>

      {/* Custom numeric keypad — opens when either amount field is tapped.
          Reuses the existing handleFromChange / handleToChange so the
          cross-conversion math is unchanged. "Maximum" always fills the
          From side with the full available balance (To recomputes via
          handleFromChange); "Clean" wipes the From side which zeroes both.
          inline mode keeps the host screen interactive — switching
          between From/To tap-targets does NOT dismiss the keypad; only a
          tap outside the keypad (handled by the ScrollView Pressable
          wrap above) closes it. */}
      <NumericKeypad
        inline
        visible={keypadField !== null}
        value={keypadField === 'from' ? fromInput : keypadField === 'to' ? toInput : ''}
        onChange={(next) => {
          amountTouched.current = true;
          if (keypadField === 'from') handleFromChange(next);
          else if (keypadField === 'to') handleToChange(next);
        }}
        onClose={() => {
          // Restore the visible "0" placeholder when the user closes the sheet
          // with an empty field — mirrors the prior onBlur behavior so the
          // form never displays a literally empty input.
          if (keypadField === 'from' && fromInput === '') setFromInput('0');
          if (keypadField === 'to' && toInput === '') setToInput('0');
          setKeypadField(null);
        }}
        availableValue={
          // Show whichever account the user is currently editing. For "to"
          // we report the card's current balance pulled from V2Balance (so
          // it works even when the Payoma cardDetail fetch hasn't landed
          // yet) — fall back to cardDetail-derived value, then to undefined
          // if nothing is known. For "from" we report the source USDT
          // balance. Max still always fills From (source of truth).
          keypadField === 'to'
            ? (() => {
                const fromCardBalance = balanceAmountNumber(selectedCard);
                if (fromCardBalance > 0) {
                  return `${formatPretty(fromCardBalance)} ${toCardCurrency || selectedCard.token_symbol}`;
                }
                if (toCardAmount != null) {
                  return `${formatPretty(toCardAmount)} ${toCardCurrency}`;
                }
                return undefined;
              })()
            : selectedFrom
              ? `${formatPretty(fromBalanceNum)} ${selectedFrom.token_symbol}`
              : undefined
        }
        onMax={
          selectedFrom
            ? () => {
              amountTouched.current = true;
              handleFromChange(numberToAmountDisplay(fromBalanceNum));
            }
            : undefined
        }
        onClean={() => {
          amountTouched.current = true;
          handleFromChange('');
        }}
      />

      <PINConfirmModal
        visible={pinModalVisible}
        title="Confirm card top-up"
        subtitle={
          selectedFrom
            ? `${amountNumber.toFixed(2)} ${selectedFrom.token_symbol} → card`
            : ''
        }
        onSuccess={executeTransfer}
        onCancel={() => setPinModalVisible(false)}
      />

      {/* Success modal — same UX as the Withdraw tab. OK closes and
          returns to whichever screen opened Operations (no intermediate
          "Back to dashboard" screen). */}
      <Modal
        visible={successModalVisible}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => {
          setSuccessModalVisible(false);
          onBack();
        }}
      >
        <View style={styles.successOverlay}>
          <View style={styles.successCard}>
            <Image
              source={require('../../assets/wallet2.png')}
              style={styles.successIcon}
              resizeMode="contain"
            />
            <Text style={styles.successTitle}>Request sent</Text>
            <Text style={styles.successSubtitle}>
              Operation is being processed.
            </Text>
            <View style={styles.successAmountRow}>
              <Text style={styles.successAmount}>
                -{formatPretty(submittedAmount)}
              </Text>
              <Text style={styles.successCurrency}> {submittedCurrency}</Text>
            </View>
            <TouchableOpacity
              style={styles.successButton}
              onPress={() => {
                setSuccessModalVisible(false);
                onBack();
              }}
              activeOpacity={0.8}
            >
              <Text style={styles.successButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// ──────────────────────────────────────────────────
// AccountSelectorModal — bottom sheet
// ──────────────────────────────────────────────────

interface AccountSelectorModalProps {
  visible: boolean;
  balances: V2Balance[];
  selectedId: string | null;
  onSelect: (b: V2Balance) => void;
  onClose: () => void;
}

const AccountSelectorModal: React.FC<AccountSelectorModalProps> = ({
  visible,
  balances,
  selectedId,
  onSelect,
  onClose,
}) => {
  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <Pressable style={styles.sheetBackdrop} onPress={onClose}>
        <Pressable style={styles.sheetContainer} onPress={(e) => e.stopPropagation()}>
          <View style={styles.sheetHandle} />
          <Text style={styles.sheetTitle}>Current account</Text>

          {balances.length === 0 ? (
            <Text style={styles.sheetEmpty}>No accounts available</Text>
          ) : (
            balances.map((b) => {
              const isSelected = b.id === selectedId;
              return (
                <TouchableOpacity
                  key={b.id}
                  style={[styles.sheetRow, isSelected && styles.sheetRowSelected]}
                  activeOpacity={0.7}
                  onPress={() => onSelect(b)}
                >
                  <TokenIcon symbol={b.token_symbol} iconUrl={b.icon_url} />
                  <View style={styles.sheetRowTextCol}>
                    <Text style={styles.sheetRowTitle}>
                      {b.token_name || b.token_symbol}, {b.token_symbol}
                    </Text>
                    {b.value_usd ? (
                      <Text style={styles.sheetRowSubtitle}>
                        {formatPretty(parseFloat(b.value_usd))} USDT
                      </Text>
                    ) : null}
                  </View>
                  <Text style={styles.sheetRowAmount}>{formatPretty(balanceAmountNumber(b))}</Text>
                </TouchableOpacity>
              );
            })
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
};

// ──────────────────────────────────────────────────
// TokenIcon — small helper that falls back to a text glyph
// ──────────────────────────────────────────────────

const TokenIcon: React.FC<{ symbol: string; iconUrl?: string | null }> = ({ symbol, iconUrl }) => {
  if (iconUrl) {
    return <Image source={{ uri: iconUrl }} style={styles.tokenIcon} />;
  }
  return (
    <View style={[styles.tokenIcon, styles.tokenIconPlaceholder]}>
      <Text style={styles.tokenIconGlyph}>{symbol?.charAt(0) ?? '?'}</Text>
    </View>
  );
};

// ──────────────────────────────────────────────────
// Styles
// ──────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  fixedHeader: {
    backgroundColor: COLORS.primaryNavy,
  },
  bottomOverscrollCap: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 500,
    backgroundColor: COLORS.pureLight,
  },
  scrollView: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  formLoading: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
  },
  scrollContent: {
    flexGrow: 1,
    backgroundColor: COLORS.pureLight,
    // Round the top corners of the light content area so the navy header
    // → pureLight body transition matches WithdrawScreen / ConfirmDepositScreen
    // (both use 12 dp on their respective content containers).
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 140,
  },

  // ── Form wrapper ──────────────────────────────────
  formCard: {
    backgroundColor: COLORS.pureLight,
    borderRadius: 8,
    paddingVertical: 12,
  },

  fieldLabel: {
    fontFamily: 'Inter',
    fontSize: 16,
    lineHeight: 22,
    color: COLORS.blueSteel,
    fontWeight: '400',
    // 8 dp below the label feeds the gap to the picker tile underneath.
    // Previously provided by the (now removed) `fieldLabelRow` wrapper
    // that hosted the read-only Radio direction-marker.
    marginBottom: 8,
  },
  fieldLabelSpaced: {
    // 0 — the gap above "To card" is provided entirely by the
    // pickerTile.marginBottom (8 dp) above. Mirrors WithdrawScreen
    // where addressLabelRow.marginTop is 0 and only networkSelector's
    // marginBottom feeds the gap. Combined with fieldLabel's own
    // marginBottom: 8, this gives equal 8 dp above and below the
    // "To card" label, matching Withdraw's "Wallet address".
    marginTop: 0,
  },

  // ── "From current account" dropdown row ───────────
  fromBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
    ...CARD_SURFACE,
    marginBottom: 0,
  },
  fromTextCol: {
    flex: 1,
    marginLeft: 10,
  },
  fromTokenName: {
    fontFamily: 'Inter',
    fontSize: 18,
    lineHeight: 22,
    fontWeight: '600',
    color: COLORS.primaryNavy,
  },
  fromSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    lineHeight: 18,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  chevronDown: {
    width: 20,
    height: 20,
    marginLeft: 8,
    transform: [{ rotate: '-90deg' }],
  },

  // ── "To card" row ─────────────────────────────────
  toCardBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    ...CARD_SURFACE,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  toCardCurrencySymbol: {
    fontFamily: 'Inter',
    fontSize: 26,
    color: COLORS.primaryNavy,
    marginRight: 10,
    fontWeight: '600',
  },
  toCardTextCol: {
    flex: 1,
  },
  toCardCurrency: {
    fontFamily: 'Inter',
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '400',
    color: COLORS.primaryNavy,
  },
  toCardNumber: {
    fontFamily: 'Inter',
    fontSize: 14,
    lineHeight: 18,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  toCardAmount: {
    fontFamily: 'Inter',
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '400',
    color: COLORS.primaryNavy,
  },

  // ── Inline editable amount (used in From/To blocks) ────────────────────
  // Right-aligned input + currency suffix that sits inside the From/To row.
  // Replaces the old standalone "Transfer amount" block.
  inlineAmountInputWrap: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginLeft: 12,
  },
  inlineAmountInput: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 20,
    color: COLORS.textPrimary,
    textAlign: 'right',
    minWidth: 64,
    padding: 0,
  },
  inlineAmountSuffix: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 20,
    color: COLORS.textPrimary,
    marginLeft: 6,
  },

  // ── "Available" row under From block ───────────────────────────────────
  availableRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    // 0/16: marginTop is 0 because the To-pickerTile above owns the 16 dp
    // gap; marginBottom is 16 so the gap to the next element (info card
    // or expectedFrame) stays uniform regardless of whether the info
    // card is rendered.
    marginTop: 0,
    marginBottom: 16,
  },
  availableLabel: {
    fontFamily: 'Inter',
    fontSize: 13,
    lineHeight: 18,
    color: COLORS.blueSteel,
  },
  availableValue: {
    fontFamily: 'Inter',
    fontSize: 13,
    lineHeight: 18,
    color: COLORS.blueSteel,
  },

  // ── Disclaimer text ───────────────────────────────
  disclaimer: {
    fontFamily: 'Inter',
    fontSize: 14,
    lineHeight: 18,
    color: COLORS.blueSteel,
    marginTop: 8,
  },

  // ── Transfer button ───────────────────────────────
  transferBtn: {
    marginTop: 20,
    backgroundColor: COLORS.primaryNavy,
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  transferBtnDisabled: {
    backgroundColor: COLORS.blueSteel,
  },
  transferBtnText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.white,
  },

  // ── Token icon (shared) ───────────────────────────
  tokenIcon: {
    width: 28,
    height: 28,
    borderRadius: 14,
  },
  tokenIconPlaceholder: {
    backgroundColor: COLORS.steelNavy,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tokenIconGlyph: {
    fontFamily: 'Inter',
    color: COLORS.white,
    fontSize: 16,
    fontWeight: '700',
  },

  // ── Bottom sheet ──────────────────────────────────
  sheetBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(24, 29, 39, 0.5)',
    justifyContent: 'flex-end',
  },
  sheetContainer: {
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 24,
  },
  sheetHandle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: COLORS.borderMuted,
    alignSelf: 'center',
    marginBottom: 12,
  },
  sheetTitle: {
    fontFamily: 'Inter',
    fontSize: 22,
    lineHeight: 26,
    fontWeight: '600',
    color: COLORS.primaryNavy,
    marginBottom: 12,
  },
  sheetEmpty: {
    fontFamily: 'Inter',
    fontSize: 16,
    color: COLORS.blueSteel,
    textAlign: 'center',
    paddingVertical: 20,
  },
  sheetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.white,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    paddingHorizontal: 12,
    paddingVertical: 12,
    marginBottom: 8,
  },
  sheetRowSelected: {
    borderColor: COLORS.brandOrange,
    borderWidth: 1.5,
  },
  sheetRowTextCol: {
    flex: 1,
    marginLeft: 10,
  },
  sheetRowTitle: {
    fontFamily: 'Inter',
    fontSize: 17,
    lineHeight: 22,
    fontWeight: '600',
    color: COLORS.primaryNavy,
  },
  sheetRowSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    lineHeight: 18,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  sheetRowAmount: {
    fontFamily: 'Inter',
    fontSize: 17,
    lineHeight: 22,
    fontWeight: '500',
    color: COLORS.primaryNavy,
  },
  /* ── Account / card pickers (Withdraw-style tiles) ──────────────────── */
  pickerTile: {
    ...CARD_SURFACE,
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    paddingVertical: 14,
    marginBottom: 8,
  },
  pickerTileCenter: {
    justifyContent: 'center',
  },
  pickerTextCol: {
    flex: 1,
    marginLeft: 10,
    justifyContent: 'center',
  },
  /* Title row holds the picker title + an inline chevron sitting on the
     same baseline (chevron pulled left next to the title rather than
     floating at the far right of the tile). */
  pickerTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  pickerChevronInline: {
    width: 16,
    height: 16,
    marginLeft: 4,
    transform: [{ rotate: '-90deg' }],
  },
  pickerTitle: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  pickerSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  pickerAmount: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
    marginLeft: 8,
    textAlign: 'right',
  },
  /* Tap target on the left of the From tile that opens the picker modal.
     Wraps icon + name; the amount input sits outside so the keyboard isn't
     dismissed by the modal-opening touch. */
  pickerSelectArea: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  /* Inline amount input on the right of the From tile. Right-aligned to
     mirror the formatted-amount layout used elsewhere. */
  pickerAmountInput: {
    minWidth: 120,
    maxWidth: 200,
    fontFamily: 'Inter',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 20,
    color: COLORS.textPrimary,
    textAlign: 'right',
    padding: 0,
  },
  pickerAmountSuffix: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 20,
    color: COLORS.textPrimary,
  },
  /* Row that wraps amount + cursor + suffix into one tap target so the
     currency label is part of the clickable area. */
  pickerAmountTapArea: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  /* Blinking cursor: a 2-dp wide vertical line matching the amount text
     baseline. Visibility is toggled by a setInterval in <BlinkingCursor>. */
  /* Insufficient-funds tint applied to BOTH the amount input and its
     currency suffix in the From tile. Same hue as `insufficientFundsText`
     below so the entire row reads as the error state. */
  amountError: {
    color: COLORS.brandOrange,
  },
  /* ── Success modal (post-transfer) — clone of WithdrawScreen. ──────── */
  successOverlay: {
    flex: 1,
    backgroundColor: COLORS.overlay,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  successCard: {
    width: '100%',
    backgroundColor: COLORS.white,
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 32,
    alignItems: 'center',
  },
  successIcon: {
    width: 56,
    height: 56,
    marginBottom: 16,
  },
  successTitle: {
    fontFamily: 'Helvetica Neue',
    fontSize: 24,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 28,
    color: COLORS.textPrimary,
    textAlign: 'center',
  },
  successSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 20,
    color: COLORS.blueSteel,
    textAlign: 'center',
    marginTop: 8,
  },
  successAmountRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginTop: 24,
    marginBottom: 32,
  },
  successAmount: {
    fontFamily: 'Helvetica Neue',
    fontSize: 32,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 48,
    color: COLORS.textPrimary,
  },
  successCurrency: {
    fontFamily: 'Helvetica Neue',
    fontSize: 24,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 28,
    color: COLORS.textPrimary,
    marginBottom: 6,
  },
  successButton: {
    alignSelf: 'stretch',
    height: 48,
    backgroundColor: COLORS.primaryNavy,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successButtonText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.white,
  },
  /* Inline error rendered below the From tile when the user types more
     than the available balance. Brand-orange (the design's red) to draw
     attention without an additional banner widget. */
  insufficientFundsText: {
    fontFamily: 'Inter',
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 16,
    color: COLORS.brandOrange,
    marginTop: 6,
    marginBottom: 8,
    paddingHorizontal: 4,
  },
  pickerChevron: {
    width: 20,
    height: 20,
    marginLeft: 8,
    transform: [{ rotate: '-90deg' }],
  },
  /* Amount input row (used for separate From / To EUR fields). */
  amountRow: {
    ...CARD_SURFACE,
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    marginBottom: 8,
  },
  amountRowInput: {
    flex: 1,
    fontFamily: 'Inter',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 20,
    color: COLORS.textPrimary,
    textAlign: 'right',
  },
  amountRowSuffix: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 20,
    color: COLORS.textPrimary,
    marginLeft: 6,
  },
  /* Picker bottom-sheet modal. Overlay stays transparent — the user
     wanted the list to slide up over the form without dimming the rest
     of the screen. Tap on the transparent area still closes the modal
     because the Pressable's onPress fires regardless of fill. */
  modalOverlay: {
    flex: 1,
    backgroundColor: 'transparent',
    justifyContent: 'flex-end',
  },
  modalSheet: {
    // flex:1 stretches the sheet to the full overlay height so the top
    // of the picker aligns with the top of the screen (matches the
    // Withdraw / Inflow screens — single behaviour across Operations).
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 32,
  },
  modalHandle: {
    width: 40,
    height: 4,
    backgroundColor: COLORS.softGrey,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 16,
  },
  modalTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  modalTitle: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
    color: COLORS.textPrimary,
  },
  modalTitleAside: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.blueSteel,
  },
  modalEmpty: {
    fontFamily: 'Inter',
    fontSize: 14,
    color: COLORS.blueSteel,
    paddingHorizontal: 12,
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    marginBottom: 8,
    backgroundColor: COLORS.white,
  },
  modalOptionSelected: {
    borderColor: COLORS.brandOrange,
  },
  modalOptionText: {
    flex: 1,
    marginLeft: 10,
    justifyContent: 'center',
  },
  modalOptionName: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  modalOptionSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  modalOptionAmount: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
    marginLeft: 8,
    textAlign: 'right',
  },
  modalCheck: {
    fontSize: 20,
    color: COLORS.mint,
    fontWeight: '600',
    marginLeft: 8,
  },
  /* Combined Fee + Minimum amount tile — clone of WithdrawScreen.infoCard. */
  infoCard: {
    ...CARD_SURFACE,
    paddingHorizontal: 14,
    paddingVertical: 6,
    // 0/16: previous element (To-pickerTile or availableRow) owns the
    // 16 dp top gap; this tile owns the gap to expectedFrame below.
    // Was 16/16, double-counted with siblings above.
    marginTop: 0,
    marginBottom: 16,
  },
  /* "Flat" variant — used for the Min / Rate / Fee block so it blends
     with the pureLight screen background instead of standing up as a
     white tile. Keeps the muted border + inner dividers. Mirrors the
     same modifier on WithdrawScreen / OpenDepositScreen. */
  infoCardFlat: {
    backgroundColor: COLORS.pureLight,
  },
  infoCardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 38,
  },
  infoCardLabel: {
    // Без flex:1, чтобы длинные лейблы вроде "Currency rate, USDT/EUR"
    // не упирались в 50% ширины и не переносились. flexShrink:0 не даёт
    // тексту сжиматься; ширина — по содержимому.
    flexShrink: 0,
    marginRight: 8,
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.blueSteel,
  },
  infoCardValue: {
    // flex:1 — отдаём правой стороне всё что осталось от лейбла.
    flex: 1,
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.blueSteel,
    textAlign: 'right',
  },
  infoCardDivider: {
    height: 1,
    backgroundColor: COLORS.borderMuted,
  },
  /* Expected-processing callout — clone of WithdrawScreen.expectedFrame. */
  expectedFrame: {
    ...CARD_SURFACE,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 16,
  },
  expectedIcon: {
    width: 16,
    height: 16,
    marginRight: 10,
    tintColor: COLORS.steelNavy,
  },
  expectedText: {
    flex: 1,
    fontFamily: 'Inter',
    fontSize: 12,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 14,
    color: COLORS.steelNavy,
    textAlign: 'left',
  },
  expectedStrong: {
    fontFamily: 'Inter',
    fontSize: 12,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 14,
    color: COLORS.steelNavy,
  },
  /* Confirm button — clone of WithdrawScreen.transferButton (primary-navy). */
  confirmBtn: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
    gap: 12,
    height: 48,
    backgroundColor: COLORS.primaryNavy,
    borderRadius: 8,
    alignSelf: 'stretch',
  },
  confirmBtnDisabled: {
    opacity: 0.5,
  },
  confirmBtnText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.white,
  },
});

export default DepositCardScreen;
